/* Book Lab writing workspace — structured block editor at
   /book-lab/editor?bookId=<id>&view=writer */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Loader2, Wand2, PlayCircle, Download, FileText, Book,
} from "lucide-react";
import { api } from "../../lib/apiClient";
import { ExportBookModal } from "./ExportBookModal";
import BookSetupFlow from "./setup/BookSetupFlow";
import { bookLabHomeUrl, bookLabCanvasUrl, resolveBookId } from "../../lib/bookLabRoutes";
import {
  resolveChapterBlocks,
  blocksToPlainText,
  wordCountFromBlocks,
  countBlockType,
  updateBlockInList,
  applyStyleToBlocks,
  makeBlock,
  legacyContentToBlocks,
  extractClaimsFromBlocks,
  DEFAULT_BOOK_STYLE_TOKENS,
} from "./writer/chapterBlocks";
import ChapterPageEditor from "./writer/ChapterPageEditor";
import WriteAssistantPanel from "./writer/WriteAssistantPanel";
import StylesAssistantPanel from "./writer/StylesAssistantPanel";
import ImagesAssistantPanel from "./writer/ImagesAssistantPanel";
import SourcesAssistantPanel from "./writer/SourcesAssistantPanel";
import "./writer/bookWritingWorkspace.css";

const PAGE_VIEW_KEY = "premierlab.bookLab.pageView";

function statusLabel(status) {
  const map = {
    planned: "Not generated", pending: "Not generated", generating: "Generating",
    draft: "Draft", completed: "Draft", approved: "Approved", failed: "Failed",
  };
  return map[status] || status || "Pending";
}

function statusIcon(status) {
  const map = {
    planned: "○", pending: "○", generating: "◌",
    draft: "●", completed: "●", approved: "✓", failed: "!",
  };
  return map[status] || "○";
}

function chapterLabel(ch, index) {
  if (ch.label) return ch.label;
  if (ch.type === "introduction") return "Introduction";
  if (ch.type === "conclusion") return "Conclusion";
  if (ch.type === "author_bio") return "Author bio";
  if (ch.type === "back_cover") return "Back cover";
  if (ch.type === "appendix") return "Appendix";
  return `Chapter ${ch.order || index + 1}`;
}

function loadPageView() {
  try {
    const v = localStorage.getItem(PAGE_VIEW_KEY);
    if (v === "paper" || v === "dark" || v === "match") return v;
  } catch (_) { /* ignore */ }
  return "paper";
}

export function BookLabProjectWorkspace({
  bookId,
  nav,
  embedded = false,
  initialChapterId = null,
  onChapterChange,
  onBlockSelect,
  onRequestCanvas,
}) {
  const resolvedId = resolveBookId(bookId) || bookId;
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedChapterId, setSelectedChapterId] = useState(initialChapterId || null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [job, setJob] = useState(null);
  const [flash, setFlash] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [busy, setBusy] = useState({});
  const [editDirty, setEditDirty] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const [showExport, setShowExport] = useState(false);
  const [assistantTab, setAssistantTab] = useState("write");
  const [confirmModal, setConfirmModal] = useState(null);
  const [canvasModal, setCanvasModal] = useState(false);
  const [pageView, setPageView] = useState(loadPageView);
  const [editorMode, setEditorMode] = useState("write");
  const [bookTokens, setBookTokens] = useState(DEFAULT_BOOK_STYLE_TOKENS);
  const [draftTokens, setDraftTokens] = useState(DEFAULT_BOOK_STYLE_TOKENS);
  const [styleClipboard, setStyleClipboard] = useState(null);
  const [imageForm, setImageForm] = useState({
    prompt: "", imageType: "editorial illustration", visualStyle: "",
    orientation: "landscape", placement: "inline", caption: "", alt: "",
  });
  const [imageBusy, setImageBusy] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [sources, setSources] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claimSummary, setClaimSummary] = useState(null);
  const [citationStyle, setCitationStyle] = useState("APA");
  const [citationsText, setCitationsText] = useState("");
  const [suggestScope, setSuggestScope] = useState("chapter");
  const [attachScope, setAttachScope] = useState("chapter");
  const [sourcesBusy, setSourcesBusy] = useState(null);
  const [sourcesError, setSourcesError] = useState(null);
  const [improveBusy, setImproveBusy] = useState(null);
  const [scrollToBlockId, setScrollToBlockId] = useState(null);
  const autosaveTimer = useRef(null);
  const editRevision = useRef(0);
  const blocksRef = useRef([]);
  const dirtyRef = useRef(false);
  const selectedRef = useRef(null);
  const migratingRef = useRef(new Set());

  const load = useCallback(async () => {
    setErr(null);
    try {
      const b = await api.get(`/api/book-lab/books/${resolvedId}`);
      setBook(b);
      const tokens = { ...DEFAULT_BOOK_STYLE_TOKENS, ...(b.style_tokens || b.book_styles || {}) };
      setBookTokens(tokens);
      setDraftTokens(tokens);
      if (b.citation_style) setCitationStyle(b.citation_style);
      setSelectedChapterId((prev) => {
        if (initialChapterId && b.chapters?.some((c) => c.id === initialChapterId)) {
          return initialChapterId;
        }
        if (prev && b.chapters?.some((c) => c.id === prev)) return prev;
        return b.chapters?.[0]?.id || null;
      });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [resolvedId, initialChapterId]);

  const loadSources = useCallback(async () => {
    try {
      const r = await api.get(`/api/book-lab/books/${resolvedId}/sources`);
      setSources(r.items || []);
    } catch (_) {
      /* endpoint may be new; keep local list */
    }
  }, [resolvedId]);

  const loadMedia = useCallback(async () => {
    try {
      const r = await api.get(`/api/book-lab/books/${resolvedId}/assets`);
      setMediaLibrary((r.items || []).filter((a) => a.kind === "image" && a.status !== "replaced"));
    } catch (_) { /* ignore */ }
  }, [resolvedId]);

  useEffect(() => { load(); loadSources(); loadMedia(); }, [load, loadSources, loadMedia]);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const s = await api.get(`/api/book-lab/books/${resolvedId}/generation-status`);
        if (cancelled) return;
        setJob(s.job);
        if (s.job && ["queued", "generating"].includes(s.job.status)) {
          const b = await api.get(`/api/book-lab/books/${resolvedId}`);
          setBook(b);
          setTimeout(poll, 4000);
        } else if (s.job) {
          const b = await api.get(`/api/book-lab/books/${resolvedId}`);
          setBook(b);
        }
      } catch (_) { /* silent */ }
    }
    poll();
    return () => { cancelled = true; };
  }, [resolvedId]);

  const selectedChapter = useMemo(
    () => book?.chapters?.find((c) => c.id === selectedChapterId) || null,
    [book, selectedChapterId]
  );

  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );

  useEffect(() => {
    selectedRef.current = selectedChapter;
  }, [selectedChapter]);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    if (!selectedChapter) {
      setBlocks([]);
      setSelectedBlockId(null);
      return;
    }
    const resolved = resolveChapterBlocks(selectedChapter);
    setBlocks(resolved);
    blocksRef.current = resolved;
    setSelectedBlockId(null);
    setEditDirty(false);
    dirtyRef.current = false;
    setSaveState("saved");

    // Persist migrated blocks once for legacy chapters (content without blocks)
    const needsMigrate = !Array.isArray(selectedChapter.blocks) || !selectedChapter.blocks.length;
    if (needsMigrate && (selectedChapter.content || "").trim() && !migratingRef.current.has(selectedChapter.id)) {
      migratingRef.current.add(selectedChapter.id);
      api.patch(`/api/book-lab/books/${resolvedId}/chapters/${selectedChapter.id}`, {
        blocks: resolved,
        content: selectedChapter.content,
        plain_text_content: selectedChapter.content,
      }).catch(() => {}).finally(() => {
        migratingRef.current.delete(selectedChapter.id);
      });
    }
  }, [selectedChapterId, selectedChapter?.updated_at, selectedChapter?.id, resolvedId]);

  useEffect(() => {
    try { localStorage.setItem(PAGE_VIEW_KEY, pageView); } catch (_) { /* ignore */ }
  }, [pageView]);

  const setB = (k) => (v) => setBusy((b) => ({ ...b, [k]: v }));

  const persistChapter = useCallback(async (chapter, nextBlocks) => {
    if (!chapter) return;
    const rev = ++editRevision.current;
    setSaveState("saving");
    const plain = blocksToPlainText(nextBlocks);
    try {
      await api.patch(`/api/book-lab/books/${resolvedId}/chapters/${chapter.id}`, {
        blocks: nextBlocks,
        content: plain,
        plain_text_content: plain,
      });
      if (rev !== editRevision.current) return;
      setEditDirty(false);
      dirtyRef.current = false;
      setSaveState("saved");
      setBook((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          chapters: (prev.chapters || []).map((c) =>
            c.id === chapter.id
              ? { ...c, blocks: nextBlocks, content: plain, plain_text_content: plain, updated_at: new Date().toISOString() }
              : c
          ),
        };
      });
    } catch (e) {
      setSaveState("failed");
      setFlash({ err: e.message || "Save failed" });
    }
  }, [resolvedId]);

  const flushSave = useCallback(async () => {
    if (!dirtyRef.current || !selectedRef.current) return;
    await persistChapter(selectedRef.current, blocksRef.current);
  }, [persistChapter]);

  useEffect(() => {
    if (!editDirty || !selectedChapter) return undefined;
    setSaveState("saving");
    window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      persistChapter(selectedChapter, blocksRef.current).catch(() => {});
    }, 900);
    return () => window.clearTimeout(autosaveTimer.current);
  }, [blocks, editDirty, selectedChapter, persistChapter]);

  useEffect(() => () => {
    window.clearTimeout(autosaveTimer.current);
  }, []);

  const markDirtyBlocks = (next) => {
    setBlocks(next);
    blocksRef.current = next;
    setEditDirty(true);
    dirtyRef.current = true;
  };

  const onChangeBlock = (blockId, patch) => {
    markDirtyBlocks(updateBlockInList(blocksRef.current, blockId, patch));
  };

  const selectChapter = async (id) => {
    if (id === selectedChapterId) return;
    window.clearTimeout(autosaveTimer.current);
    await flushSave();
    setSelectedChapterId(id);
    onChapterChange?.(id);
  };

  useEffect(() => {
    onBlockSelect?.(selectedBlockId);
  }, [selectedBlockId, onBlockSelect]);

  const generateOutline = async () => {
    setB("outline")(true); setFlash(null);
    try {
      const r = await api.post(`/api/book-lab/books/${resolvedId}/generate-outline`);
      setFlash({ ok: `Outline generated: ${r.chapters?.length || 0} chapters` });
      await load();
    } catch (e) { setFlash({ err: e.message }); }
    finally { setB("outline")(false); setConfirmModal(null); }
  };

  const requestRegenerateOutline = () => {
    setConfirmModal({
      type: "regen-outline",
      title: "Regenerate outline?",
      body: "This may replace the current chapter structure. Existing chapter content can be lost.",
      onConfirm: generateOutline,
    });
  };

  const generateChapter = async (chapterId, { force } = {}) => {
    const ch = book?.chapters?.find((c) => c.id === chapterId);
    const hasContent = (ch?.content || "").trim() || (ch?.blocks || []).length;
    if (ch && (ch.status === "completed" || ch.status === "approved" || hasContent) && !force) {
      setConfirmModal({
        type: "regen-chapter",
        title: "Regenerate chapter?",
        body: "This chapter already has content. Regenerating will replace it.",
        onConfirm: () => generateChapter(chapterId, { force: true }),
      });
      return;
    }
    setConfirmModal(null);
    setB(`ch-${chapterId}`)(true); setFlash(null);
    try {
      const r = await api.post(`/api/book-lab/books/${resolvedId}/generate-chapter`, { chapter_id: chapterId });
      setFlash({ ok: `Chapter generated (${r.used_provider || ""})` });
      await load();
    } catch (e) { setFlash({ err: e.message }); }
    finally { setB(`ch-${chapterId}`)(false); }
  };

  const approveChapter = async () => {
    if (!selectedChapter) return;
    await flushSave();
    setB("approve")(true);
    try {
      await api.patch(`/api/book-lab/books/${resolvedId}/chapters/${selectedChapter.id}`, { status: "approved" });
      setFlash({ ok: "Chapter marked approved" });
      await load();
    } catch (e) { setFlash({ err: e.message }); }
    finally { setB("approve")(false); }
  };

  const generateRemaining = async () => {
    const pending = (book?.chapters || []).filter(
      (c) => !["completed", "approved", "generating"].includes(c.status)
        && !(c.content || "").trim()
        && !(c.blocks || []).length
    );
    setConfirmModal(null);
    if (!pending.length) {
      setFlash({ ok: "No pending chapters — approved/completed sections were skipped." });
      return;
    }
    setB("remaining")(true); setFlash(null);
    try {
      for (const ch of pending) {
        await api.post(`/api/book-lab/books/${resolvedId}/generate-chapter`, { chapter_id: ch.id });
        await load();
      }
      setFlash({ ok: `Generated ${pending.length} chapter(s)` });
    } catch (e) { setFlash({ err: e.message }); }
    finally { setB("remaining")(false); }
  };

  const requestGenerateRemaining = () => {
    const pending = (book?.chapters || []).filter(
      (c) => !["completed", "approved", "generating"].includes(c.status)
        && !(c.content || "").trim()
        && !(c.blocks || []).length
    );
    setConfirmModal({
      type: "remaining",
      title: "Generate remaining chapters",
      body: `PremierLab will generate ${pending.length} remaining section(s). Approved and completed chapters are skipped.`,
      onConfirm: generateRemaining,
    });
  };

  const goHome = async () => {
    await flushSave().catch(() => {});
    nav(bookLabHomeUrl());
  };

  const openCanvas = async () => {
    await flushSave().catch(() => {});
    try {
      if (book && !book.canvas_document_id) {
        await api.patch(`/api/book-lab/books/${resolvedId}`, { canvas_document_id: resolvedId });
      }
      await api.get(`/api/book-lab/books/${resolvedId}/pages`);
    } catch (_) { /* canvas can still open */ }
    setCanvasModal(false);
    if (onRequestCanvas) {
      onRequestCanvas();
      return;
    }
    nav(bookLabCanvasUrl(resolvedId));
  };

  const exportPdf = async () => {
    await flushSave().catch(() => {});
    setShowExport(true);
  };

  const insertBlock = (type, content = {}, { style, afterId = selectedBlockId } = {}) => {
    const block = makeBlock(type, content, style || {});
    const list = [...blocksRef.current];
    const idx = afterId ? list.findIndex((b) => b.id === afterId) : -1;
    if (idx >= 0) list.splice(idx + 1, 0, block);
    else list.push(block);
    markDirtyBlocks(list);
    setSelectedBlockId(block.id);
    return block;
  };

  const onImprove = async (action) => {
    if (!selectedBlock || !selectedChapter) return;
    setImproveBusy(action);
    setFlash(null);
    try {
      const r = await api.post(
        `/api/book-lab/books/${resolvedId}/chapters/${selectedChapter.id}/improve-block`,
        { block_id: selectedBlock.id, action, block: selectedBlock }
      );
      if (r.block) {
        markDirtyBlocks(updateBlockInList(blocksRef.current, selectedBlock.id, {
          content: r.block.content,
          type: r.block.type || selectedBlock.type,
        }));
      } else if (r.blocks) {
        markDirtyBlocks(r.blocks);
      } else if (r.content != null) {
        markDirtyBlocks(updateBlockInList(blocksRef.current, selectedBlock.id, {
          content: { ...selectedBlock.content, text: r.content },
        }));
      }
      setFlash({ ok: `${action.replace(/_/g, " ")} applied` });
    } catch (e) {
      setFlash({ err: e.message || "Improve action failed" });
    } finally {
      setImproveBusy(null);
    }
  };

  /* ── Styles ── */
  const onApplySelected = (tokenKey, stylePatch, appliesTo) => {
    if (!selectedBlock) return;
    if (appliesTo && !appliesTo.includes(selectedBlock.type) && tokenKey !== "body") {
      setFlash({ err: `Selected block is ${selectedBlock.type}; pick a matching style row or apply to chapter.` });
      return;
    }
    markDirtyBlocks(updateBlockInList(blocksRef.current, selectedBlock.id, { style: stylePatch }));
    setFlash({ ok: "Style applied to selected block" });
  };

  const onApplyChapter = (tokenKey, stylePatch, appliesTo) => {
    const next = applyStyleToBlocks(
      blocksRef.current,
      stylePatch,
      (b) => !appliesTo || appliesTo.includes(b.type) || (tokenKey === "body" && b.type === "paragraph")
    );
    markDirtyBlocks(next);
    setFlash({ ok: "Style applied to this chapter" });
  };

  const onApplyBook = async (tokenKey, stylePatch) => {
    setB("style-book")(true);
    try {
      const nextTokens = { ...bookTokens, [tokenKey]: stylePatch };
      await api.patch(`/api/book-lab/books/${resolvedId}`, { style_tokens: nextTokens });
      setBookTokens(nextTokens);
      setDraftTokens(nextTokens);
      setFlash({ ok: "Style saved to entire book" });
    } catch (e) {
      setFlash({ err: e.message || "Failed to save book styles" });
    } finally {
      setB("style-book")(false);
    }
  };

  const onResetStyle = (tokenKey) => {
    const bookStyle = bookTokens[tokenKey] || DEFAULT_BOOK_STYLE_TOKENS[tokenKey];
    setDraftTokens({ ...draftTokens, [tokenKey]: { ...bookStyle } });
    if (selectedBlock) {
      markDirtyBlocks(updateBlockInList(blocksRef.current, selectedBlock.id, { style: {} }));
    }
    setFlash({ ok: "Reset to book style" });
  };

  const onCopyChapterStyle = () => {
    const styles = {};
    blocksRef.current.forEach((b) => {
      if (b.style && Object.keys(b.style).length) styles[b.id] = { type: b.type, style: b.style };
    });
    setStyleClipboard({ tokens: draftTokens, blockStyles: styles });
    setFlash({ ok: "Chapter style copied" });
  };

  const onPasteChapterStyle = () => {
    if (!styleClipboard) return;
    setDraftTokens(styleClipboard.tokens || draftTokens);
    let next = blocksRef.current;
    if (styleClipboard.blockStyles) {
      next = next.map((b) => {
        const match = Object.values(styleClipboard.blockStyles).find((x) => x.type === b.type);
        return match ? { ...b, style: { ...match.style } } : b;
      });
      markDirtyBlocks(next);
    }
    setFlash({ ok: "Chapter style pasted" });
  };

  const onSavePreset = async () => {
    setB("style-preset")(true);
    try {
      await api.patch(`/api/book-lab/books/${resolvedId}`, {
        style_tokens: draftTokens,
        book_styles: draftTokens,
      });
      setBookTokens(draftTokens);
      setFlash({ ok: "Saved as book style preset" });
    } catch (e) {
      setFlash({ err: e.message });
    } finally {
      setB("style-preset")(false);
    }
  };

  /* ── Images ── */
  const assetUrl = (id) => `/api/book-lab/books/${resolvedId}/assets/${id}`;

  const runImageGen = async (prompt, size, { replaceAssetId } = {}) => {
    const r = await api.post(`/api/book-lab/books/${resolvedId}/generate-image`, {
      prompt,
      size: size || imageForm.orientation || "landscape",
      chapter_id: selectedChapter?.id,
      replace_asset_id: replaceAssetId,
    });
    await loadMedia();
    return r;
  };

  const onGenerateChapterImage = async () => {
    setImageError(null);
    setImageBusy("gen-chapter");
    const prompt = imageForm.prompt
      || `Chapter opener illustration for "${selectedChapter?.title || book?.title}" in ${imageForm.visualStyle || "editorial"} style. No heavy text.`;
    try {
      const r = await runImageGen(prompt);
      insertBlock("image", {
        url: r.url, assetId: r.id, prompt: r.prompt, caption: imageForm.caption || "",
        alt: imageForm.alt || r.prompt, placement: imageForm.placement || "full_width",
        imageType: imageForm.imageType, status: "ready",
      });
      setFlash({ ok: "Chapter image generated" });
    } catch (e) {
      setImageError(e.message || "Image generation unavailable");
      setFlash({ err: e.message || "Image generation failed" });
    } finally {
      setImageBusy(null);
    }
  };

  const onGenerateSectionImage = async () => {
    if (!selectedBlock) return;
    setImageError(null);
    setImageBusy("gen-section");
    const ctx = selectedBlock.content?.text || selectedBlock.content?.title || "";
    const prompt = imageForm.prompt || `Illustration for: ${ctx.slice(0, 200)}. Style: ${imageForm.visualStyle || "editorial"}. No dense text.`;
    try {
      const r = await runImageGen(prompt);
      insertBlock("image", {
        url: r.url, assetId: r.id, prompt: r.prompt, caption: imageForm.caption || "",
        alt: imageForm.alt || "", placement: imageForm.placement || "inline", status: "ready",
      });
      setFlash({ ok: "Section image generated" });
    } catch (e) {
      setImageError(e.message || "Image generation unavailable");
    } finally {
      setImageBusy(null);
    }
  };

  const onGenerateDiagram = () => {
    const title = imageForm.prompt?.split(/—|–|-/)[0]?.trim() || "Diagram";
    const nodes = (imageForm.prompt || "Start → Next → Goal")
      .split(/→|->|›|,/)
      .map((s) => s.replace(/^\[DIAGRAM:\s*/i, "").replace(/\]$/, "").trim())
      .filter(Boolean)
      .slice(0, 12);
    insertBlock("diagram", {
      title,
      nodes: nodes.length > 1 ? nodes : ["Step 1", "Step 2", "Step 3"],
      diagramType: imageForm.imageType === "timeline" ? "timeline" : "roadmap",
    });
    setFlash({ ok: "Diagram inserted — labels are editable on the page" });
  };

  const uploadImageFile = async (file, { asReference } = {}) => {
    setImageError(null);
    setImageBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", asReference ? "reference" : "image");
      if (selectedChapter?.id) fd.append("chapter_id", selectedChapter.id);
      let r;
      try {
        r = await api.upload(`/api/book-lab/books/${resolvedId}/assets/upload`, fd);
      } catch (uploadErr) {
        // Fallback: read as data URL for local insert if upload endpoint missing
        if (uploadErr.status === 404) {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          insertBlock("image", {
            url: dataUrl, caption: imageForm.caption || file.name, alt: imageForm.alt || file.name,
            placement: imageForm.placement || "inline", status: "ready",
            prompt: asReference ? `Reference: ${file.name}` : file.name,
          });
          setFlash({ ok: asReference ? "Reference image attached locally" : "Image uploaded into chapter" });
          return;
        }
        throw uploadErr;
      }
      insertBlock("image", {
        url: r.url || assetUrl(r.id), assetId: r.id, caption: imageForm.caption || file.name,
        alt: imageForm.alt || file.name, placement: imageForm.placement || "inline", status: "ready",
        prompt: asReference ? `Reference: ${file.name}` : file.name,
      });
      await loadMedia();
      setFlash({ ok: asReference ? "Reference image saved" : "Image uploaded" });
    } catch (e) {
      setImageError(e.message || "Upload failed");
      setFlash({ err: e.message || "Upload failed" });
    } finally {
      setImageBusy(null);
    }
  };

  const onGenerateVariations = async () => {
    if (!selectedBlock?.content?.url) return;
    setImageBusy("variations");
    setImageError(null);
    try {
      const base = selectedBlock.content.prompt || imageForm.prompt || "variation";
      const r = await runImageGen(`${base} — variation`, imageForm.orientation, {
        replaceAssetId: undefined,
      });
      insertBlock("image", {
        url: r.url, assetId: r.id, prompt: r.prompt,
        caption: selectedBlock.content.caption || "",
        alt: selectedBlock.content.alt || "",
        placement: selectedBlock.content.placement || "inline",
        status: "ready",
        variationOf: selectedBlock.content.assetId,
      });
      setFlash({ ok: "Variation generated" });
    } catch (e) {
      setImageError(e.message || "Variation generation unavailable");
    } finally {
      setImageBusy(null);
    }
  };

  const onReplaceSelected = async () => {
    if (!selectedBlock || !["image", "diagram"].includes(selectedBlock.type)) return;
    if (selectedBlock.type === "diagram") {
      onGenerateDiagram();
      markDirtyBlocks(blocksRef.current.filter((b) => b.id !== selectedBlock.id));
      return;
    }
    setImageBusy("replace");
    try {
      const prompt = imageForm.prompt || selectedBlock.content.prompt || "replacement image";
      const r = await runImageGen(prompt, imageForm.orientation, {
        replaceAssetId: selectedBlock.content.assetId,
      });
      onChangeBlock(selectedBlock.id, {
        content: {
          ...selectedBlock.content,
          url: r.url, assetId: r.id, prompt: r.prompt, status: "ready",
          caption: imageForm.caption || selectedBlock.content.caption,
          alt: imageForm.alt || selectedBlock.content.alt,
          placement: imageForm.placement || selectedBlock.content.placement,
        },
      });
      setFlash({ ok: "Image replaced" });
    } catch (e) {
      setImageError(e.message || "Replace failed");
    } finally {
      setImageBusy(null);
    }
  };

  const onRemoveSelected = () => {
    if (!selectedBlock) return;
    markDirtyBlocks(blocksRef.current.filter((b) => b.id !== selectedBlock.id));
    setSelectedBlockId(null);
    setFlash({ ok: "Removed from chapter" });
  };

  const onSaveToLibrary = async () => {
    if (!selectedBlock?.content?.assetId) return;
    try {
      await api.patch(`/api/book-lab/books/${resolvedId}/assets/${selectedBlock.content.assetId}`, {
        chapter_id: selectedChapter?.id,
      });
      await loadMedia();
      setFlash({ ok: "Saved to book media library" });
    } catch (e) {
      setFlash({ err: e.message });
    }
  };

  const onConvertPlaceholders = () => {
    const text = blocksToPlainText(blocksRef.current);
    const converted = legacyContentToBlocks(text, { chapterTitle: selectedChapter?.title });
    // Drop duplicate chapter_title if already present
    const withoutDupTitle = converted.filter((b, i) => !(i === 0 && b.type === "chapter_title" && blocksRef.current[0]?.type === "chapter_title"));
    const merged = blocksRef.current[0]?.type === "chapter_title"
      ? [blocksRef.current[0], ...withoutDupTitle.filter((b) => b.type !== "chapter_title")]
      : converted;
    markDirtyBlocks(merged);
    setFlash({ ok: "Placeholders converted to image/diagram blocks" });
  };

  const onInsertFromLibrary = (item) => {
    insertBlock("image", {
      url: item.url || assetUrl(item.id),
      assetId: item.id,
      prompt: item.prompt || "",
      caption: imageForm.caption || "",
      alt: imageForm.alt || item.prompt || "",
      placement: imageForm.placement || "inline",
      status: "ready",
    });
  };

  /* ── Sources ── */
  const onSuggestSources = async () => {
    setSourcesBusy("suggest");
    setSourcesError(null);
    try {
      const payload = {
        scope: suggestScope,
        chapter_id: selectedChapter?.id,
        block_id: selectedBlockId,
        text: selectedBlock?.content?.text || "",
      };
      const r = await api.post(`/api/book-lab/books/${resolvedId}/sources/suggest`, payload);
      const items = r.items || [];
      setSources((prev) => {
        const ids = new Set(prev.map((s) => s.id));
        return [...prev, ...items.filter((s) => !ids.has(s.id))];
      });
      setFlash({
        ok: items.length
          ? `${items.length} suggestion(s) — marked unverified until you confirm`
          : "No suggestions from attached research. Upload research first.",
      });
    } catch (e) {
      setSourcesError(e.message || "Source suggestion unavailable");
      setFlash({ err: e.message || "Source suggestion failed" });
    } finally {
      setSourcesBusy(null);
    }
  };

  const onUploadResearch = async ({ file, url, notes }) => {
    setSourcesBusy("upload-research");
    setSourcesError(null);
    try {
      let r;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("attach_scope", attachScope);
        if (selectedChapter?.id) fd.append("chapter_id", selectedChapter.id);
        if (selectedBlockId) fd.append("block_id", selectedBlockId);
        r = await api.upload(`/api/book-lab/books/${resolvedId}/sources/upload`, fd);
      } else {
        r = await api.post(`/api/book-lab/books/${resolvedId}/sources`, {
          title: url || "Research notes",
          url: url || null,
          notes: notes || null,
          source_type: url ? "url" : "notes",
          attach_scope: attachScope,
          chapter_id: selectedChapter?.id,
          block_id: selectedBlockId,
        });
      }
      setSources((prev) => [r, ...prev.filter((s) => s.id !== r.id)]);
      if (attachScope === "block" && selectedBlockId && r.id) {
        onChangeBlock(selectedBlockId, {
          sources: [...(selectedBlock?.sources || []), { sourceId: r.id }],
        });
      }
      setFlash({ ok: "Research attached" });
      await loadSources();
    } catch (e) {
      setSourcesError(e.message || "Research upload failed");
      setFlash({ err: e.message || "Research upload failed" });
    } finally {
      setSourcesBusy(null);
    }
  };

  const onReviewClaims = async () => {
    setSourcesBusy("claims");
    setSourcesError(null);
    try {
      let result;
      try {
        result = await api.post(`/api/book-lab/books/${resolvedId}/chapters/${selectedChapter.id}/review-claims`, {
          blocks: blocksRef.current,
        });
      } catch (apiErr) {
        // Local heuristic fallback when backend endpoint errors — still real claim records
        const local = extractClaimsFromBlocks(blocksRef.current);
        result = {
          claims: local,
          summary: {
            total: local.length,
            verified: local.filter((c) => c.status === "verified").length,
            needs_review: local.filter((c) => c.status === "needs_review").length,
            unsupported: local.filter((c) => c.status === "unsupported").length,
          },
        };
        if (apiErr.status && apiErr.status !== 404) {
          setSourcesError(apiErr.message);
        }
      }
      setClaims(result.claims || []);
      setClaimSummary(result.summary || null);
      setFlash({ ok: `Reviewed ${(result.claims || []).length} claim(s)` });
    } catch (e) {
      setSourcesError(e.message);
    } finally {
      setSourcesBusy(null);
    }
  };

  const onSelectClaim = (claim) => {
    if (claim.blockId) {
      setSelectedBlockId(claim.blockId);
      setScrollToBlockId(claim.blockId);
      setTimeout(() => setScrollToBlockId(null), 800);
    }
  };

  const onCreateCitations = async () => {
    setSourcesBusy("citations");
    try {
      const r = await api.post(`/api/book-lab/books/${resolvedId}/citations`, {
        style: citationStyle,
        scope: "chapter",
        chapter_id: selectedChapter?.id,
      });
      setCitationsText(r.text || r.citations || "");
      if (r.incomplete) {
        setFlash({ ok: "Citation list created — some entries have incomplete metadata" });
      } else {
        setFlash({ ok: "Citation list created" });
      }
    } catch (e) {
      // Build honest local citations from stored source metadata only
      const lines = sources.map((s) => {
        const parts = [];
        if (s.author) parts.push(s.author);
        else parts.push("[Author missing]");
        if (s.date || s.published_date) parts.push(`(${s.date || s.published_date})`);
        else parts.push("(n.d.)");
        parts.push(s.title || s.filename || "[Title missing]");
        if (s.publisher) parts.push(s.publisher);
        if (s.url) parts.push(s.url);
        return `${parts.join(". ")}.`;
      });
      setCitationsText(lines.join("\n") || "No sources with citation metadata yet.");
      if (e.status && e.status !== 404) setSourcesError(e.message);
      setFlash({ ok: "Citations built from stored metadata (incomplete fields marked)" });
    } finally {
      setSourcesBusy(null);
    }
  };

  const onAttachSourceToBlock = (sourceId) => {
    if (!selectedBlockId) {
      setFlash({ err: "Select a block first" });
      return;
    }
    const existing = selectedBlock?.sources || [];
    if (existing.some((s) => s.sourceId === sourceId)) {
      setFlash({ ok: "Source already attached" });
      return;
    }
    onChangeBlock(selectedBlockId, { sources: [...existing, { sourceId }] });
    setFlash({ ok: "Source attached to block" });
  };

  /* ── Toolbar insert helpers ── */
  const toolbarInsert = (type) => {
    if (type === "paragraph") insertBlock("paragraph", { text: "" });
    else if (type === "bullet_list") insertBlock("bullet_list", { items: [""] });
    else if (type === "quote") insertBlock("quote", { text: "", attribution: "" });
    else if (type === "heading_1") insertBlock("heading_1", { text: "" });
    else if (type === "callout") insertBlock("callout", { label: "NOTE", text: "" }, { style: { variant: "information" } });
    else if (type === "page_break") insertBlock("page_break", {});
  };

  if (loading) {
    return (
      <div className="book-writing-workspace" data-testid="book-lab-project">
        <div className="bww-editor-empty"><Loader2 className="animate-spin" size={18} /> Loading book…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="book-writing-workspace" data-testid="book-lab-project">
        <div className="bww-editor-empty">
          Error: {err} · <Link to={bookLabHomeUrl()} style={{ color: "#22C55E" }}>Back</Link>
        </div>
      </div>
    );
  }
  if (!book) {
    return (
      <div className="book-writing-workspace" data-testid="book-lab-project">
        <div className="bww-editor-empty">Book not found.</div>
      </div>
    );
  }

  const chapters = book.chapters || [];
  const total = chapters.length;
  const generated = chapters.filter((c) => !["planned", "pending"].includes(c.status)).length;
  const approved = chapters.filter((c) => c.status === "approved").length;
  const pct = total ? Math.round((generated / total) * 100) : 0;
  const words = wordCountFromBlocks(blocks);
  const images = countBlockType(blocks, "image") + (selectedChapter?.images?.length || 0);
  const tables = countBlockType(blocks, "table");
  const hasOutline = !!book.outline_generated || total > 0;
  const canvasWords = chapters.reduce((n, c) => n + wordCountFromBlocks(resolveChapterBlocks(c)), 0);
  const canvasImages = chapters.reduce((n, c) => n + countBlockType(resolveChapterBlocks(c), "image") + (c.images?.length || 0), 0);
  const canvasTables = chapters.reduce((n, c) => n + countBlockType(resolveChapterBlocks(c), "table"), 0);
  const pageViewLabel = pageView === "paper" ? "Paper" : pageView === "dark" ? "Dark" : "Match app theme";

  return (
    <div
      className={`book-writing-workspace${embedded ? " book-writing-workspace--embedded" : ""}`}
      data-testid="book-lab-project"
    >
      {!embedded && (
      <header className="bww-topbar">
        <button type="button" className="bww-back" onClick={goHome} data-testid="bl-back">
          ←<br />Book Lab
        </button>
        <div className="bww-book-meta">
          <div className="bww-title-row">
            <h1>{book.title || "Untitled Book"}</h1>
            <span className="bww-status-pill" data-testid="bl-status-pill">{book.status || "draft"}</span>
          </div>
          <p>{book.subtitle || book.author || ""}</p>
        </div>
        <div className="bww-top-actions">
          <span className={`bww-save-state ${saveState}`} data-testid="bl-save-state">
            {saveState === "saving" ? "● Saving…" : saveState === "failed" ? "● Error saving" : "● All changes saved"}
          </span>
          {job && ["queued", "generating"].includes(job.status) && (
            <span data-testid="bl-job-progress" style={{ fontSize: 12, color: "#91a49d" }}>
              <Loader2 className="animate-spin" size={12} style={{ display: "inline" }} />{" "}
              Generating {job.chapters_completed}/{job.chapters_total}
            </span>
          )}
          <button type="button" className="bww-btn" data-testid="bl-open-setup" onClick={() => setShowSetup(true)}>
            <Book size={13} /> Book setup
          </button>
          <button type="button" className="bww-btn" data-testid="bl-open-styles" onClick={() => setAssistantTab("styles")}>
            Book styles
          </button>
          <div className="bww-page-view" data-testid="bl-page-view">
            <label htmlFor="bl-page-view-select">Page: {pageViewLabel}</label>
            <select
              id="bl-page-view-select"
              data-testid="bl-page-view-select"
              value={pageView}
              onChange={(e) => setPageView(e.target.value)}
            >
              <option value="paper">Paper</option>
              <option value="dark">Dark</option>
              <option value="match">Match app theme</option>
            </select>
          </div>
          {!hasOutline ? (
            <button type="button" className="bww-btn primary" data-testid="bl-generate-outline"
              onClick={generateOutline} disabled={busy.outline}>
              {busy.outline ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />} Generate outline
            </button>
          ) : (
            <>
              <button type="button" className="bww-btn" data-testid="bl-regenerate-outline"
                onClick={requestRegenerateOutline} disabled={busy.outline}>
                Regenerate outline
              </button>
              <button type="button" className="bww-btn" data-testid="bl-generate-remaining"
                onClick={requestGenerateRemaining} disabled={busy.remaining}>
                {busy.remaining ? <Loader2 className="animate-spin" size={13} /> : <PlayCircle size={13} />} Generate remaining
              </button>
            </>
          )}
          <button type="button" className="bww-btn" data-testid="bl-export-pdf" onClick={exportPdf}>
            <Download size={13} /> Preview
          </button>
          <button type="button" className="bww-btn" data-testid="bl-open-in-canvas"
            onClick={() => setCanvasModal(true)}>
            <FileText size={13} /> Open in Canvas
          </button>
        </div>
      </header>
      )}
      {embedded && (
        <div className="bww-embedded-bar" data-testid="bl-embedded-bar">
          <span className={`bww-save-state ${saveState}`} data-testid="bl-save-state">
            {saveState === "saving" ? "● Saving…" : saveState === "failed" ? "● Error saving" : "● All changes saved"}
          </span>
          <div className="bww-page-view" data-testid="bl-page-view">
            <label htmlFor="bl-page-view-select">Page: {pageViewLabel}</label>
            <select
              id="bl-page-view-select"
              data-testid="bl-page-view-select"
              value={pageView}
              onChange={(e) => setPageView(e.target.value)}
            >
              <option value="paper">Paper</option>
              <option value="dark">Dark</option>
              <option value="match">Match app theme</option>
            </select>
          </div>
          <button type="button" className="bww-btn" data-testid="bl-open-setup" onClick={() => setShowSetup(true)}>
            Book setup
          </button>
          <button type="button" className="bww-btn" data-testid="bl-open-styles" onClick={() => setAssistantTab("styles")}>
            Book styles
          </button>
          {!hasOutline ? (
            <button type="button" className="bww-btn primary" data-testid="bl-generate-outline"
              onClick={generateOutline} disabled={busy.outline}>
              Generate outline
            </button>
          ) : (
            <button type="button" className="bww-btn" data-testid="bl-generate-remaining"
              onClick={requestGenerateRemaining} disabled={busy.remaining}>
              Generate remaining
            </button>
          )}
          <button type="button" className="bww-btn" data-testid="bl-open-in-canvas"
            onClick={() => (onRequestCanvas ? setCanvasModal(true) : setCanvasModal(true))}>
            Open in Canvas
          </button>
        </div>
      )}

      {flash && (
        <div className={`bww-flash ${flash.err ? "err" : "ok"}`} data-testid="bl-flash">
          {flash.err || flash.ok}
        </div>
      )}

      <div className="bww-workspace">
        <aside className="bww-left" data-testid="bl-chapter-list">
          <div className="bww-rail-header">
            <strong>Chapters</strong>
            <span data-testid="bl-chapter-progress">{generated} of {total} generated</span>
          </div>
          {!total ? (
            <div className="bww-outline-empty" data-testid="bl-outline-empty">
              No outline yet. Click <strong>Generate outline</strong> to create the chapter plan,
              or reopen Book setup if this book was created from a blueprint without chapters.
            </div>
          ) : (
            chapters.map((c, i) => (
              <button
                key={c.id}
                type="button"
                className={`bww-chapter-row${selectedChapterId === c.id ? " active" : ""}`}
                data-testid={`bl-chapter-item-${c.id}`}
                onClick={() => selectChapter(c.id)}
              >
                <div>
                  <span className="bww-chapter-title">{c.title}</span>
                  <span className="bww-chapter-meta">
                    <span>{chapterLabel(c, i)}</span>
                    <span>{wordCountFromBlocks(resolveChapterBlocks(c))} words</span>
                    <span>{countBlockType(resolveChapterBlocks(c), "image") + (c.images?.length || 0)} images</span>
                    <span>{countBlockType(resolveChapterBlocks(c), "table")} tables</span>
                  </span>
                </div>
                <span className={`bww-chapter-status ${c.status || "planned"}`}>
                  {statusIcon(c.status)} {statusLabel(c.status)}
                </span>
              </button>
            ))
          )}
        </aside>

        <main className="bww-editor-shell">
          {!selectedChapter ? (
            <section className="bww-editor-empty" data-testid="bl-editor-empty">
              <div className="bww-empty-card">
                <div className="bww-empty-icon">✦</div>
                <h2>Your writing workspace is ready</h2>
                <p>
                  Generate the outline first. Then create, format, review, illustrate, and approve
                  each chapter before sending the manuscript to Canvas.
                </p>
                {!hasOutline && (
                  <button type="button" className="bww-btn primary" onClick={generateOutline} disabled={busy.outline}>
                    {busy.outline ? <Loader2 className="animate-spin" size={13} /> : null} Generate book outline
                  </button>
                )}
              </div>
            </section>
          ) : (
            <section className="bww-editor-view" data-testid="bl-editor-view">
              <div className="bww-chapter-header">
                <div className="bww-chapter-heading">
                  <small>{chapterLabel(selectedChapter, 0)}</small>
                  <h2>{selectedChapter.title}</h2>
                </div>
                <div className="bww-mode-tabs" data-testid="bl-editor-modes">
                  {["write", "design", "preview"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`bww-mode-tab${editorMode === m ? " active" : ""}`}
                      data-testid={`bl-mode-${m}`}
                      onClick={() => setEditorMode(m === "preview" ? "preview" : m)}
                    >
                      {m === "preview" ? "Page Preview" : m[0].toUpperCase() + m.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bww-content-card bww-page-stage">
                <div className="bww-chapter-goal">
                  <strong>Chapter goal</strong>
                  <p data-testid="bl-chapter-goal">
                    {selectedChapter.goal || selectedChapter.hook || selectedChapter.description
                      || "No goal saved for this chapter yet."}
                  </p>
                </div>
                <div className="bww-editor-toolbar">
                  <button type="button" className="bww-toolbar-btn" data-testid="bl-insert-heading"
                    disabled={editorMode === "preview"} onClick={() => toolbarInsert("heading_1")}>Heading</button>
                  <button type="button" className="bww-toolbar-btn" data-testid="bl-insert-paragraph"
                    disabled={editorMode === "preview"} onClick={() => toolbarInsert("paragraph")}>Paragraph</button>
                  <button type="button" className="bww-toolbar-btn" data-testid="bl-insert-bullets"
                    disabled={editorMode === "preview"} onClick={() => toolbarInsert("bullet_list")}>Bullets</button>
                  <button type="button" className="bww-toolbar-btn" data-testid="bl-insert-quote"
                    disabled={editorMode === "preview"} onClick={() => toolbarInsert("quote")}>Quote</button>
                  <button type="button" className="bww-toolbar-btn" data-testid="bl-insert-callout"
                    disabled={editorMode === "preview"} onClick={() => toolbarInsert("callout")}>Callout</button>
                  <button type="button" className="bww-toolbar-btn" data-testid="bl-insert-pagebreak"
                    disabled={editorMode === "preview"} onClick={() => toolbarInsert("page_break")}>Page break</button>
                </div>
                <div className="bww-page-scroll" data-testid="bl-chapter-editor">
                  <ChapterPageEditor
                    blocks={blocks}
                    bookTokens={bookTokens}
                    selectedBlockId={selectedBlockId}
                    onSelectBlock={setSelectedBlockId}
                    onChangeBlock={onChangeBlock}
                    pageView={pageView}
                    editorMode={editorMode === "design" ? "design" : editorMode}
                    scrollToBlockId={scrollToBlockId}
                    headerConfig={book.header_config}
                    footerConfig={book.footer_config}
                    chapterTitle={selectedChapter.title}
                    bookTitle={book.title}
                    pageNumber={selectedChapter.order || 1}
                  />
                </div>
              </div>
            </section>
          )}
        </main>

        <aside className="bww-right" data-testid="bl-chapter-assistant">
          <div className="bww-rail-header">
            <strong>Chapter assistant</strong>
            <span>{assistantTab}</span>
          </div>
          <div className="bww-assistant-tabs">
            {["write", "styles", "images", "sources"].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`bww-assistant-tab${assistantTab === tab ? " active" : ""}`}
                data-testid={`bl-assistant-tab-${tab}`}
                onClick={() => setAssistantTab(tab)}
              >
                {tab[0].toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {assistantTab === "write" && (
            <WriteAssistantPanel
              generated={generated}
              approved={approved}
              pct={pct}
              words={words}
              statusLabel={statusLabel(selectedChapter?.status)}
              images={images}
              selectedChapter={selectedChapter}
              selectedBlock={selectedBlock}
              busy={busy}
              onGenerateChapter={generateChapter}
              onRegenerateChapter={(id) => generateChapter(id, { force: true })}
              onApprove={approveChapter}
              onRecommendVisuals={() => setAssistantTab("images")}
              onImprove={onImprove}
              improveBusy={improveBusy}
            />
          )}

          {assistantTab === "styles" && (
            <StylesAssistantPanel
              bookTokens={bookTokens}
              draftTokens={draftTokens}
              onDraftChange={setDraftTokens}
              selectedBlock={selectedBlock}
              busy={busy["style-book"] || busy["style-preset"] || busy["header-footer"]}
              onApplySelected={onApplySelected}
              onApplyChapter={onApplyChapter}
              onApplyBook={onApplyBook}
              onReset={onResetStyle}
              onCopyChapterStyle={onCopyChapterStyle}
              onPasteChapterStyle={onPasteChapterStyle}
              canPaste={!!styleClipboard}
              onSavePreset={onSavePreset}
              headerConfig={book?.header_config}
              footerConfig={book?.footer_config}
              onHeaderFooterChange={({ header, footer }) => {
                setBook((prev) => prev ? {
                  ...prev,
                  ...(header ? { header_config: header } : {}),
                  ...(footer ? { footer_config: footer } : {}),
                } : prev);
              }}
              onSaveHeaderFooter={async () => {
                setB("header-footer")(true);
                try {
                  await api.patch(`/api/book-lab/books/${resolvedId}`, {
                    header_config: book.header_config,
                    footer_config: book.footer_config,
                  });
                  setFlash({ ok: "Header / footer saved to book styles" });
                } catch (e) {
                  setFlash({ err: e.message });
                } finally {
                  setB("header-footer")(false);
                }
              }}
            />
          )}

          {assistantTab === "images" && (
            <ImagesAssistantPanel
              selectedBlock={selectedBlock}
              chapterTitle={selectedChapter?.title}
              busy={imageBusy}
              form={imageForm}
              onFormChange={setImageForm}
              onGenerateChapterImage={onGenerateChapterImage}
              onGenerateSectionImage={onGenerateSectionImage}
              onGenerateDiagram={onGenerateDiagram}
              onUpload={(f) => uploadImageFile(f)}
              onUseReference={(f) => uploadImageFile(f, { asReference: true })}
              onGenerateVariations={onGenerateVariations}
              onReplaceSelected={onReplaceSelected}
              onRemoveSelected={onRemoveSelected}
              onSaveToLibrary={onSaveToLibrary}
              onConvertPlaceholders={onConvertPlaceholders}
              mediaLibrary={mediaLibrary}
              onInsertFromLibrary={onInsertFromLibrary}
              error={imageError}
            />
          )}

          {assistantTab === "sources" && (
            <SourcesAssistantPanel
              selectedBlock={selectedBlock}
              sources={sources}
              claims={claims}
              claimSummary={claimSummary}
              citationStyle={citationStyle}
              onCitationStyleChange={async (v) => {
                setCitationStyle(v);
                try {
                  await api.patch(`/api/book-lab/books/${resolvedId}`, { citation_style: v });
                } catch (_) { /* preference still local */ }
              }}
              citationsText={citationsText}
              busy={sourcesBusy}
              error={sourcesError}
              onSuggestSources={onSuggestSources}
              suggestScope={suggestScope}
              onSuggestScopeChange={setSuggestScope}
              onUploadResearch={onUploadResearch}
              attachScope={attachScope}
              onAttachScopeChange={setAttachScope}
              onReviewClaims={onReviewClaims}
              onSelectClaim={onSelectClaim}
              onCreateCitations={onCreateCitations}
              onAttachSourceToBlock={onAttachSourceToBlock}
            />
          )}
        </aside>
      </div>

      {confirmModal && (
        <div className="bww-modal-backdrop" role="dialog" aria-modal="true" data-testid="bl-confirm-modal">
          <div className="bww-modal">
            <h2>{confirmModal.title}</h2>
            <p>{confirmModal.body}</p>
            <div className="bww-modal-actions">
              <button type="button" className="bww-btn" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button type="button" className="bww-btn primary" data-testid="bl-confirm-ok"
                onClick={() => confirmModal.onConfirm?.()}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {canvasModal && (
        <div className="bww-modal-backdrop" role="dialog" aria-modal="true" data-testid="bl-canvas-modal">
          <div className="bww-modal">
            <h2>Prepare manuscript for Canvas</h2>
            <p>Structured text, tables, callouts, and approved images will travel into Canvas with this book.</p>
            <div className="bww-metric-grid" style={{ marginTop: 14 }}>
              <div className="bww-metric"><span>Sections</span><strong>{total}</strong></div>
              <div className="bww-metric"><span>Words</span><strong>{canvasWords}</strong></div>
              <div className="bww-metric"><span>Images</span><strong>{canvasImages}</strong></div>
              <div className="bww-metric"><span>Tables</span><strong>{canvasTables}</strong></div>
            </div>
            <div className="bww-modal-actions">
              <button type="button" className="bww-btn" onClick={() => setCanvasModal(false)}>Cancel</button>
              <button type="button" className="bww-btn primary" data-testid="bl-canvas-continue" onClick={openCanvas}>
                Continue to Canvas
              </button>
            </div>
          </div>
        </div>
      )}

      {showSetup && (
        <BookSetupFlow
          bookId={resolvedId}
          initialBook={book}
          onClose={() => setShowSetup(false)}
          onCreated={() => { setShowSetup(false); load(); }}
        />
      )}
      {showExport && (
        <ExportBookModal bookId={resolvedId} bookTitle={book.title} onClose={() => setShowExport(false)} />
      )}
    </div>
  );
}

export default function BookLabProject(props) {
  const nav = useNavigate();

  return (
    <BookLabProjectWorkspace
      {...props}
      nav={nav}
    />
  );
}
